export const useTeamsStore = defineStore('teamsStore', () => {
    const { user, session } = useUserSession();
    const { public: publicConfig } = useRuntimeConfig();

    const teams = ref([]);
    const mainTeam = ref(null);

    const initialized = ref(false);

    // Holds the current active/selected team context
    const currentTeam = ref(null);

    const getTeamByUuid = (uuid) => {
        if (!uuid) {
            return null;
        }

        return teams.value.find((team) => team.uuid === uuid) || null;
    };

    const isCurrentTeamMain = computed(() => {
        return currentTeam.value?.is_main ?? false;
    });

    const currentTeamUuid = computed(() => {
        return currentTeam.value?.uuid ?? null;
    });

    const hasLegacyCertificates = computed(() => {
        return currentTeam.value?.has_legacy_certificates ?? false;
    });

    /**
     * Update the last used team in the backend
     *
     * @param {string} teamUuid - The UUID of the team to set as last used
     * @returns {Promise<Response>}
     */
    const updateLastUsedTeam = async (teamUuid) => {
        const { public: publicConfig } = useRuntimeConfig();

        // Skip if no access token is available
        if (!session.value?.accessToken) {
            return;
        }

        const withAuthHeader = session.value.accessToken;

        const Authorization = withAuthHeader.startsWith('Bearer ')
            ? withAuthHeader
            : `Bearer ${withAuthHeader}`;

        const response = await $fetch(
            `${publicConfig.appHost}/api/v1/update-last-used`,
            {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization,
                    'X-Context-Platform-Public-Auth-Token':
                        publicConfig.platform.authToken,
                },
                body: JSON.stringify({
                    type: 'team',
                    value: teamUuid,
                }),
            },
        );

        return response;
    };

    /**
     * Fetch teams from the backend
     *
     * @param {string} withAuthHeader - Whether to include the authentication token in the request
     * @returns {Promise<void>}
     */
    const fetchTeams = async (withAuthHeader) => {
        const Authorization = withAuthHeader.startsWith('Bearer ')
            ? withAuthHeader
            : `Bearer ${withAuthHeader}`;

        const [_error, response] = await catchError(
            $fetch(`${publicConfig.appHost}/api/v1/teams`, {
                method: 'GET',
                headers: {
                    Authorization,
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Context-Platform-Public-Auth-Token':
                        publicConfig.platform.authToken,
                },
            }),
        );

        if (!response.data) {
            return;
        }

        const { data } = response;

        teams.value = data ?? [];
        initialized.value = true;
        mainTeam.value = teams.value.filter((team) => team.is_main)[0] || null;
    };

    const setActiveTeam = (uuid = null) => {
        // Check if we need to send an update
        const sendUpdate = uuid !== null && uuid !== user.value?.lastUsedTeam;
        if (uuid === null) {
            uuid = user.value?.lastUsedTeam;
        }

        if (!teams.value || teams.value.length === 0) {
            console.warn('No teams available to set active team.');
            return;
        }

        // Get the team from uuid
        const team = getTeamByUuid(uuid);
        if (!team) {
            console.warn(`Team with UUID ${uuid} not found.`);
            return;
        }

        currentTeam.value = team;

        // Update the last used team
        if (sendUpdate) {
            updateLastUsedTeam(uuid);
        }
    };

    /**
     * Load the products for the user
     *
     * @param {string} withAuthHeader - If true, fetch the products with auth
     * @param {string} activeTeamAfter - If provided, set the active team after loading
     * @returns {Promise<void>}
     */
    const loadTeams = async (
        withAuthHeader = undefined,
        activeTeamAfter = undefined,
        force = false,
    ) => {
        if (initialized.value && !force) {
            return;
        }

        const authHeader = withAuthHeader
            ? withAuthHeader
            : session.value?.accessToken;

        // Fetch the teams for the user
        await fetchTeams(authHeader);

        if (activeTeamAfter) {
            setActiveTeam(activeTeamAfter, teams.value);
        }
    };

    const unload = () => {
        teams.value = [];
        mainTeam.value = null;
        initialized.value = false;
        currentTeam.value = null;
    };

    return {
        teams,
        mainTeam,
        currentTeam,
        currentTeamUuid,
        hasLegacyCertificates,
        loadTeams,
        setActiveTeam,
        isCurrentTeamMain,
        unload,
        getTeamByUuid,
    };
});
